/* ==========================================================================
   Wickd Sports — wickd.js
   Cart drawer + AJAX cart, quick-add, variant selection, mobile nav,
   collection filter bottom-sheet, product recommendations.
   Vanilla, progressive enhancement. No localStorage/sessionStorage.
   ========================================================================== */
(function () {
  'use strict';

  var FREE_SHIP_THRESHOLD = window.WK_FREE_SHIP_CENTS || 10000; // $100

  /* ---------- helpers ---------- */
  function money(cents) {
    // Uses Shopify money format injected on window if available.
    if (window.Shopify && window.Shopify.formatMoney && window.WK_MONEY_FORMAT) {
      return window.Shopify.formatMoney(cents, window.WK_MONEY_FORMAT);
    }
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
  }
  function $(sel, root) { return (root || document).querySelector(sel); }
  function $all(sel, root) { return Array.prototype.slice.call((root || document).querySelectorAll(sel)); }

  var FOCUSABLE = 'a[href],area[href],button:not([disabled]),input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* ==========================================================================
     CART DRAWER
     ========================================================================== */
  var overlay = $('#WkCartOverlay');
  var drawerEl = overlay ? $('.wk-drawer', overlay) : null;
  var cartLive = $('[data-wk-cart-live]');
  var lastFocused = null; // element to return focus to on close

  function announce(msg) {
    if (cartLive) {
      cartLive.textContent = '';
      // re-set on next frame so SR re-announces identical messages
      window.requestAnimationFrame(function () { cartLive.textContent = msg; });
    }
  }

  function getFocusable(container) {
    return $all(FOCUSABLE, container).filter(function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function trapFocus(e) {
    if (!overlay || !overlay.classList.contains('is-open')) return;
    if (e.key !== 'Tab') return;
    var focusables = getFocusable(drawerEl);
    if (!focusables.length) { e.preventDefault(); return; }
    var first = focusables[0];
    var last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault(); first.focus();
    }
  }

  function openDrawer() {
    if (!overlay) return;
    if (!overlay.classList.contains('is-open')) lastFocused = document.activeElement;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    // focus the close button (or the drawer) once visible
    var closeBtn = $('[data-wk-cart-close]', drawerEl) || drawerEl;
    if (closeBtn) closeBtn.focus();
  }
  function closeDrawer() {
    if (!overlay || !overlay.classList.contains('is-open')) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
      lastFocused = null;
    }
  }

  function renderCart(cart) {
    if (!overlay) return cart;
    // count
    $all('.wk-cart-count').forEach(function (el) {
      el.textContent = cart.item_count;
      el.hidden = cart.item_count === 0;
    });
    var dc = $('[data-wk-drawer-count]', overlay);
    if (dc) dc.textContent = '(' + cart.item_count + ')';

    // free shipping meter
    var ship = $('[data-wk-ship]', overlay);
    if (ship) {
      var remaining = Math.max(0, FREE_SHIP_THRESHOLD - cart.total_price);
      if (cart.item_count === 0) {
        ship.innerHTML = '';
      } else if (remaining === 0) {
        ship.innerHTML = '<div class="wk-ship--done">✓ You’ve unlocked free shipping</div>';
      } else {
        var pct = Math.min(100, Math.round(cart.total_price / FREE_SHIP_THRESHOLD * 100));
        ship.innerHTML =
          '<div class="wk-ship"><div class="wk-ship__msg">Add ' + money(remaining) +
          ' more for free shipping</div><div class="wk-ship__track"><div class="wk-ship__fill" style="width:' + pct + '%"></div></div></div>';
      }
    }

    // items
    var list = $('[data-wk-items]', overlay);
    var empty = $('[data-wk-empty]', overlay);
    var foot = $('[data-wk-foot]', overlay);
    if (cart.item_count === 0) {
      if (list) list.innerHTML = '';
      if (empty) empty.hidden = false;
      if (foot) foot.hidden = true;
      return cart;
    }
    if (empty) empty.hidden = true;
    if (foot) foot.hidden = false;

    if (list) {
      list.innerHTML = cart.items.map(function (it) {
        var opts = (it.options_with_values || []).map(function (o) { return o.value; }).join(' · ');
        // line item properties (player name/number, design info) — skip _private keys
        var esc = function (s) { return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'); };
        var props = Object.keys(it.properties || {}).filter(function (k) {
          return k.charAt(0) !== '_' && it.properties[k];
        }).map(function (k) {
          var v = String(it.properties[k]);
          if (/^https?:\/\//.test(v)) return esc(k) + ': <a href="' + esc(v) + '" target="_blank" rel="noopener" style="text-decoration:underline">file</a>';
          return esc(k) + ': ' + esc(v);
        }).join('<br>');
        var img = it.image ? it.image.replace(/(\.[a-z]+)(\?|$)/i, '_160x200$1$2') : '';
        return (
          '<div class="wk-line" data-key="' + it.key + '">' +
            (img ? '<img class="wk-line__img" src="' + img + '" alt="' + it.product_title + '">' : '<div class="wk-line__img"></div>') +
            '<div class="wk-line__body">' +
              '<div class="wk-line__top"><span class="wk-line__name">' + it.product_title + '</span>' +
              '<button class="wk-line__remove" data-wk-remove="' + it.key + '" aria-label="Remove ' + it.product_title + '">remove</button></div>' +
              '<span class="wk-line__opt">' + opts + '</span>' +
              (props ? '<div class="wk-line__props">' + props + '</div>' : '') +
              '<div class="wk-line__bottom"><div class="wk-line__qty">' +
                '<button data-wk-dec="' + it.key + '" aria-label="Decrease quantity">–</button><span>' + it.quantity + '</span>' +
                '<button data-wk-inc="' + it.key + '" aria-label="Increase quantity">+</button></div>' +
                '<span class="wk-line__total">' + money(it.final_line_price) + '</span>' +
              '</div>' +
            '</div>' +
          '</div>'
        );
      }).join('');
    }

    var sub = $('[data-wk-subtotal]', overlay);
    if (sub) sub.textContent = money(cart.total_price);
    return cart;
  }

  function fetchCart() {
    return fetch(window.Shopify.routes.root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function addToCart(id, quantity, properties) {
    var item = { id: id, quantity: quantity || 1 };
    if (properties && Object.keys(properties).length) item.properties = properties;
    return fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ items: [item] })
    }).then(function (r) { return r.json(); }).then(function (added) {
      var title = added && (added.product_title || added.title);
      if (title) announce(title + ' added to your bag');
      return fetchCart();
    }).then(function (cart) {
      renderCart(cart); openDrawer(); return cart;
    });
  }

  function changeLine(key, quantity) {
    return fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    }).then(function (r) { return r.json(); }).then(renderCart);
  }

  /* ---------- drawer event delegation ---------- */
  document.addEventListener('click', function (e) {
    var t = e.target;

    if (t.closest('[data-wk-cart-open]')) { e.preventDefault(); fetchCart().then(renderCart).then(openDrawer); return; }
    if (t.closest('[data-wk-cart-close]')) { e.preventDefault(); closeDrawer(); return; }

    var inc = t.closest('[data-wk-inc]'); var dec = t.closest('[data-wk-dec]'); var rm = t.closest('[data-wk-remove]');
    if (inc || dec || rm) {
      var line = t.closest('.wk-line');
      var qtyEl = line && line.querySelector('.wk-line__qty span');
      var current = qtyEl ? parseInt(qtyEl.textContent, 10) : 1;
      if (inc) changeLine(inc.getAttribute('data-wk-inc'), current + 1);
      else if (dec) changeLine(dec.getAttribute('data-wk-dec'), Math.max(0, current - 1));
      else if (rm) changeLine(rm.getAttribute('data-wk-remove'), 0);
      return;
    }

    // quick add from product cards
    var quick = t.closest('[data-wk-quick-add]');
    if (quick) {
      e.preventDefault(); e.stopPropagation();
      var vid = quick.getAttribute('data-wk-quick-add');
      if (vid) { quick.disabled = true; addToCart(vid, 1).finally(function () { quick.disabled = false; }); }
      return;
    }
  });

  // close on scrim / Esc + focus trap
  if (overlay) {
    var scrim = overlay.querySelector('.wk-overlay__scrim');
    if (scrim) scrim.addEventListener('click', closeDrawer);
  }
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') {
      // close whichever overlay is open
      if (overlay && overlay.classList.contains('is-open')) { closeDrawer(); return; }
      closeFilterSheet();
      return;
    }
    trapFocus(e);
  });

  /* ==========================================================================
     PRODUCT — variant selection + add to cart (option-order agnostic)
     ========================================================================== */
  var form = $('[data-wk-product-form]');
  if (form) {
    var variantsRaw = $('[data-wk-variants]', form);
    var variantsData = variantsRaw ? JSON.parse(variantsRaw.textContent) : [];
    var idInput = $('[data-wk-variant-id]', form) || $('[name="id"]', form);

    // Option positions (1-based) for size/color if present; 0 means "not present".
    var sizePos = parseInt(form.getAttribute('data-size-position'), 10) || 0;
    var colorPos = parseInt(form.getAttribute('data-color-position'), 10) || 0;

    // selected[position] = chosen value (string). Seed from current variant id.
    var selected = {};
    (function seedSelection() {
      var startId = idInput ? parseInt(idInput.value, 10) : 0;
      var startVariant = null;
      for (var i = 0; i < variantsData.length; i++) {
        if (variantsData[i].id === startId) { startVariant = variantsData[i]; break; }
      }
      if (!startVariant && variantsData.length) startVariant = variantsData[0];
      if (startVariant && startVariant.options) {
        startVariant.options.forEach(function (val, idx) { selected[idx + 1] = val; });
      }
    })();

    function variantMatches(v, sel) {
      if (!v.options) return false;
      for (var pos in sel) {
        if (!sel.hasOwnProperty(pos)) continue;
        var idx = parseInt(pos, 10) - 1;
        if (sel[pos] != null && v.options[idx] !== sel[pos]) return false;
      }
      return true;
    }

    function currentVariant() {
      // exact match on all selected positions
      for (var i = 0; i < variantsData.length; i++) {
        if (variantMatches(variantsData[i], selected)) return variantsData[i];
      }
      return null;
    }

    // Is there ANY available variant where option at `pos` == `value`,
    // holding all OTHER currently-selected positions fixed?
    function valueAvailable(pos, value) {
      var idx = pos - 1;
      for (var i = 0; i < variantsData.length; i++) {
        var v = variantsData[i];
        if (!v.options || v.options[idx] !== value) continue;
        var ok = true;
        for (var p in selected) {
          if (!selected.hasOwnProperty(p)) continue;
          var pn = parseInt(p, 10);
          if (pn === pos) continue;
          if (selected[p] != null && v.options[pn - 1] !== selected[p]) { ok = false; break; }
        }
        if (ok && v.available) return true;
      }
      return false;
    }

    function syncUI() {
      var v = currentVariant();
      var addBtn = $('[data-wk-add]', form);
      var price = $('[data-wk-price]');
      var pill = $('[data-wk-stock-pill]');
      var sku = $('[data-wk-sku]');

      if (v) {
        if (idInput) idInput.value = v.id;
        if (price) price.textContent = money(v.price);
        if (sku && v.sku) sku.textContent = v.sku;
        if (addBtn) {
          addBtn.disabled = !v.available;
          addBtn.textContent = v.available ? ('Add to bag — ' + money(v.price)) : 'Sold out';
        }
        if (pill) pill.hidden = !v.available;
      } else {
        if (addBtn) { addBtn.disabled = true; addBtn.textContent = 'Unavailable'; }
        if (pill) pill.hidden = true;
      }

      // Disable size chips with no available variant given other selections.
      if (sizePos) {
        $all('[data-wk-size]', form).forEach(function (b) {
          var val = b.getAttribute('data-wk-size');
          var avail = valueAvailable(sizePos, val);
          b.disabled = !avail;
          b.setAttribute('aria-disabled', avail ? 'false' : 'true');
        });
      }
      // (Color swatches left clickable; selecting a sold-out color re-disables sizes.)

      // Disable generic option chips (Weight, Hand, Flex, …) with no available
      // variant given the other current selections.
      $all('[data-wk-opt-value]', form).forEach(function (b) {
        var pos = parseInt(b.getAttribute('data-wk-opt-position'), 10);
        if (!pos) return;
        var avail = valueAvailable(pos, b.getAttribute('data-wk-opt-value'));
        b.disabled = !avail;
        b.setAttribute('aria-disabled', avail ? 'false' : 'true');
      });
    }

    function setOption(pos, value, groupSelector, activeClass, labelSelector) {
      selected[pos] = value;
      $all(groupSelector, form).forEach(function (x) {
        var matches = x.getAttribute(groupSelector.indexOf('color') > -1 ? 'data-wk-color' : 'data-wk-size') === value;
        x.classList.toggle(activeClass, matches);
        if (x.hasAttribute('aria-checked')) x.setAttribute('aria-checked', matches ? 'true' : 'false');
      });
      if (labelSelector) { var lbl = $(labelSelector); if (lbl) lbl.textContent = value; }
      syncUI();
    }

    $all('[data-wk-color]', form).forEach(function (b) {
      b.addEventListener('click', function () {
        if (!colorPos) return;
        setOption(colorPos, b.getAttribute('data-wk-color'), '[data-wk-color]', 'is-active', '[data-wk-color-label]');
      });
    });
    $all('[data-wk-size]', form).forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled || !sizePos) return;
        setOption(sizePos, b.getAttribute('data-wk-size'), '[data-wk-size]', 'is-active', '[data-wk-size-label]');
      });
    });
    // Generic option chips (any option that isn't Size/Color, e.g. bat Weight).
    // Each button carries its option position, so multiple generic groups work.
    $all('[data-wk-opt-value]', form).forEach(function (b) {
      b.addEventListener('click', function () {
        if (b.disabled) return;
        var pos = parseInt(b.getAttribute('data-wk-opt-position'), 10);
        if (!pos) return;
        var value = b.getAttribute('data-wk-opt-value');
        selected[pos] = value;
        $all('[data-wk-opt-value][data-wk-opt-position="' + pos + '"]', form).forEach(function (x) {
          var matches = x === b;
          x.classList.toggle('is-active', matches);
          if (x.hasAttribute('aria-checked')) x.setAttribute('aria-checked', matches ? 'true' : 'false');
        });
        var lbl = $('[data-wk-opt-label="' + pos + '"]');
        if (lbl) lbl.textContent = value;
        syncUI();
      });
    });

    // qty stepper
    var qtyInput = $('[data-wk-qty]', form);
    var qd = $('[data-wk-qty-dec]', form); if (qd) qd.addEventListener('click', function () { qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1); });
    var qi = $('[data-wk-qty-inc]', form); if (qi) qi.addEventListener('click', function () { qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1; });

    // collect line-item properties (e.g. jersey Name / Number) from the form
    function collectProps() {
      var props = {};
      $all('[data-wk-prop]', form).forEach(function (input) {
        var m = (input.name || '').match(/^properties\[(.+)\]$/);
        var val = (input.value || '').trim();
        if (m && val) props[m[1]] = val;
      });
      return props;
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      // let native validation (e.g. required Number) block invalid submits
      if (form.checkValidity && !form.checkValidity()) { form.reportValidity(); return; }
      var v = currentVariant();
      if (!v || !v.available) return;
      var addBtn = $('[data-wk-add]', form); if (addBtn) addBtn.disabled = true;
      addToCart(v.id, parseInt(qtyInput.value, 10) || 1, collectProps())
        .finally(function () { if (addBtn) addBtn.disabled = false; });
    });

    // gallery thumbnail swap
    $all('[data-wk-thumb]').forEach(function (th) {
      th.addEventListener('click', function () {
        var main = $('[data-wk-main-img]');
        var src = th.getAttribute('data-wk-thumb');
        if (main && src) { main.src = src; main.removeAttribute('srcset'); }
        $all('[data-wk-thumb]').forEach(function (x) { x.classList.remove('is-active'); });
        th.classList.add('is-active');
      });
    });

    syncUI();
  }

  /* ==========================================================================
     PRODUCT — Shopify recommendations (progressive enhancement)
     ========================================================================== */
  (function () {
    var rec = $('[data-wk-recommendations]');
    if (!rec) return;
    var url = rec.getAttribute('data-url');
    var inner = $('.wk-recs__inner', rec);
    if (!url || !inner || !window.fetch) return;
    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var tmp = document.createElement('div');
        tmp.innerHTML = html;
        var fresh = tmp.querySelector('.wk-recs__inner');
        // The recommendations endpoint re-renders this section with
        // recommendations.performed == true. Swap only if it returned products,
        // otherwise keep the server-rendered collection fallback.
        if (fresh && fresh.querySelector('.wk-grid')) {
          inner.innerHTML = fresh.innerHTML;
        }
      })
      .catch(function () { /* keep server-rendered fallback */ });
  })();

  /* ==========================================================================
     MOBILE — hamburger nav + collection filter/sort bottom sheet
     ========================================================================== */
  var burger = $('[data-wk-burger]');
  if (burger) burger.addEventListener('click', function () {
    var nav = $('[data-wk-mobile-nav]');
    if (nav) {
      var open = nav.classList.toggle('is-open');
      burger.setAttribute('aria-expanded', open ? 'true' : 'false');
    }
  });

  /* header nav dropdowns — reflect open state on aria-expanded */
  $all('[data-wk-nav-group]').forEach(function (group) {
    var parent = group.querySelector('a[aria-haspopup]');
    if (!parent) return;
    function set(open) { parent.setAttribute('aria-expanded', open ? 'true' : 'false'); }
    group.addEventListener('mouseenter', function () { set(true); });
    group.addEventListener('mouseleave', function () { set(false); });
    group.addEventListener('focusin', function () { set(true); });
    group.addEventListener('focusout', function () {
      if (!group.contains(document.activeElement)) set(false);
    });
  });

  var filterSheet = $('[data-wk-filter-sheet]');
  var filterToggles = $all('[data-wk-filter-open],[data-wk-sort-open]');
  var lastFilterFocus = null;

  function openFilterSheet(scrollToSort) {
    if (!filterSheet) return;
    lastFilterFocus = document.activeElement;
    filterSheet.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    filterToggles.forEach(function (b) { b.setAttribute('aria-expanded', 'true'); });
    if (scrollToSort) {
      var anchor = $('#WkSortAnchor', filterSheet);
      if (anchor) anchor.scrollIntoView({ block: 'start' });
    }
    var closeBtn = $('[data-wk-filter-close]', filterSheet);
    if (closeBtn) closeBtn.focus();
  }
  function closeFilterSheet() {
    if (!filterSheet || !filterSheet.classList.contains('is-open')) return;
    filterSheet.classList.remove('is-open');
    document.body.style.overflow = '';
    filterToggles.forEach(function (b) { b.setAttribute('aria-expanded', 'false'); });
    if (lastFilterFocus && typeof lastFilterFocus.focus === 'function') { lastFilterFocus.focus(); lastFilterFocus = null; }
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-wk-filter-open]')) { e.preventDefault(); openFilterSheet(false); return; }
    if (e.target.closest('[data-wk-sort-open]')) { e.preventDefault(); openFilterSheet(true); return; }
    if (e.target.closest('[data-wk-filter-close]')) { e.preventDefault(); closeFilterSheet(); return; }
    if (filterSheet && e.target.closest('[data-wk-filter-sheet] .wk-overlay__scrim')) { closeFilterSheet(); return; }
  });

  // Mobile filter form: chips/swatches drive hidden checkboxes; submit applies.
  var mobileForm = $('[data-wk-mobile-filter-form]');
  if (mobileForm) {
    // size chip <label> wraps a hidden checkbox; toggle is-active on change.
    $all('.wk-size-chip', mobileForm).forEach(function (label) {
      var input = label.querySelector('input');
      if (!input) return;
      input.addEventListener('change', function () {
        label.classList.toggle('is-active', input.checked);
      });
    });
    // color swatch <label> wraps a hidden checkbox.
    $all('.wk-color-dot', mobileForm).forEach(function (label) {
      var input = label.querySelector('input');
      if (!input) return;
      input.addEventListener('change', function () {
        label.classList.toggle('is-active', input.checked);
      });
    });
  }

  // Focus trap for the filter sheet (reuses Tab handling)
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Tab' || !filterSheet || !filterSheet.classList.contains('is-open')) return;
    var container = $('.wk-drawer', filterSheet);
    var focusables = getFocusable(container);
    if (!focusables.length) { e.preventDefault(); return; }
    var first = focusables[0], last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* initial cart paint */
  if (window.Shopify && window.Shopify.routes) {
    fetchCart().then(renderCart).catch(function () {});
  }
})();
